import {
  TlxObjectActionMappingModel,
  UnifiedLakeHousePolicyListItemResponse,
} from "@/api";
import { UnifiedLakehousePolicy } from "@/api/temp/UnifiedLakehouseService";
import { DEMO_DATASOURCE_IDS } from "./dataSources";

type MockUnifiedLakehousePolicy = UnifiedLakehousePolicy &
  Partial<UnifiedLakeHousePolicyListItemResponse>;

const editAction: TlxObjectActionMappingModel = {
  actionName: "EDIT",
  actionDisplayName: "Edit",
  disabled: false,
};

const deleteAction: TlxObjectActionMappingModel = {
  actionName: "DELETE",
  actionDisplayName: "Delete",
  disabled: false,
};

const disableAction: TlxObjectActionMappingModel = {
  actionName: "DISABLE",
  actionDisplayName: "Disable",
  disabled: false,
};

const deployAction: TlxObjectActionMappingModel = {
  actionName: "DEPLOY",
  actionDisplayName: "Deploy",
  disabled: false,
};

const redeployAction: TlxObjectActionMappingModel = {
  actionName: "REDEPLOY",
  actionDisplayName: "Redeploy",
  disabled: false,
};

const resolveDriftAction: TlxObjectActionMappingModel = {
  actionName: "RESOLVE_DRIFT",
  actionDisplayName: "Resolve drift",
  disabled: false,
};

const selectPrivilege = { id: "1", name: "SELECT" };

const buildColumnConditionRules = (elementName: string, value: string) => [
  {
    queryContextId: "8",
    queryContextValue: "Everyone",
    selectedPrincipalValue: "Everyone",
    columnRules: [
      {
        ruleSequenceNumber: 1,
        conjunction: "OR",
        columnRules: [
          {
            ruleSequenceNumber: 1,
            elementName,
            elementDataType: "STRING",
            conditionId: "2",
            selectedConditionValue: "Equals",
            elementType: "COLUMN_NAME",
            valueType: "CONSTANT",
            valueTypeName: "Constant Value",
            value,
            conjunction: "OR",
            arguments: {},
          },
        ],
      },
    ],
  },
];

const ORDERS_PATH = "SALES_CATALOG.PUBLIC.ORDERS";

const buildRoleRules = (roleName: string) => [
  {
    queryContextId: "5",
    queryContextValue: roleName,
    selectedPrincipalValue: "role",
  },
];

// Demo trio for the canonical finance.sales.customer_orders story.
const SF = DEMO_DATASOURCE_IDS.snowflake; // ABANK_FINANCE
const DR = DEMO_DATASOURCE_IDS.dremio; // Dremio Nessie Lakehouse

export interface LakehouseSeed {
  policies: UnifiedLakehousePolicy[];
}

const policies: MockUnifiedLakehousePolicy[] = [
  {
    id: "customer-email-masking",
    name: "Customer Email Masking",
    status: "POLICY_DEPLOYED",
    lakehousePolicyType: "Column Masking",
    policyDataTypeId: "3",
    description:
      "Mask customer email addresses for non-privileged users across mapped customer order tables.",
    canonicalObject: ORDERS_PATH,
    target: "customer_email",
    rule: "Mask customer_email unless the user has customer support or finance analyst access.",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: ORDERS_PATH }],
    privilegeList: [],
    rules: buildColumnConditionRules("customer_email", "customer_email"),
    coverageStatus: "Fully Covered",
    lastUpdated: "May 29, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Masking",
      name: "MASKING",
      displayName: "Column Masking",
    },
    policyActions: [redeployAction, disableAction, editAction, deleteAction],
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Not Required", action: "No Action" },
      { dataSourceId: DR, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Not Required", action: "No Action" },
    ],
    nativePreviews: [
      {
        dataSourceId: SF,
        preview:
          "CREATE MASKING POLICY CUSTOMER_EMAIL_MASK AS (val STRING) RETURNS STRING -> CASE WHEN CURRENT_ROLE() IN ('FINANCE_ANALYST') THEN val ELSE REGEXP_REPLACE(val, '^(.).+(@.+)$', '\\1***\\2') END;",
      },
      {
        dataSourceId: DR,
        preview:
          "Apply column mask on finance.sales.customer_orders.customer_email for unauthorized roles.",
      },
    ],
  },
  {
    id: "customer-ssn-masking",
    name: "SSN Masking Policy",
    status: "POLICY_PARTIAL_DEPLOYED",
    lakehousePolicyType: "Column Masking",
    policyDataTypeId: "3",
    description:
      "Mask social security numbers on the canonical customer orders table across every mapped platform.",
    canonicalObject: "finance.sales.customer_orders",
    target: "ssn",
    rule: "Mask ssn for every user except approved finance data stewards.",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: "finance.sales.customer_orders" }],
    privilegeList: [],
    rules: buildColumnConditionRules("ssn", "ssn"),
    coverageStatus: "Drift Detected",
    lastUpdated: "May 30, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Masking",
      name: "MASKING",
      displayName: "Column Masking",
    },
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Not Required", action: "No Action" },
      { dataSourceId: DR, objectExists: true, policyStatus: "Missing", roleMappingStatus: "Not Required", action: "Deploy Policy" },
    ],
    nativePreviews: [
      {
        dataSourceId: SF,
        preview:
          "CREATE MASKING POLICY SSN_MASK AS (val STRING) RETURNS STRING -> CASE WHEN CURRENT_ROLE() IN ('FINANCE_DATA_STEWARD') THEN val ELSE '***-**-' || RIGHT(val, 4) END;",
      },
      {
        dataSourceId: DR,
        preview:
          "Apply SSN mask on finance.sales.customer_orders.ssn for non-steward roles.",
      },
    ],
    policyActions: [resolveDriftAction, deployAction, editAction, deleteAction],
  },
  {
    id: "customer-orders-row-access",
    name: "Customer Orders Row Access",
    status: "POLICY_DEPLOYED",
    lakehousePolicyType: "Row Access",
    policyDataTypeId: "1",
    description: "Allow users to view customer order rows for their assigned sales region.",
    canonicalObject: "finance.sales.customer_orders",
    target: "sales_region = West",
    rule: "sales_region IN user.sales_regions",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: "finance.sales.customer_orders" }],
    privilegeList: [],
    rules: buildColumnConditionRules("sales_region", "West"),
    coverageStatus: "Fully Covered",
    lastUpdated: "May 29, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Row Access",
      name: "ROW_ACCESS",
      displayName: "Row Access",
    },
    policyActions: [redeployAction, disableAction, editAction, deleteAction],
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Mapped", action: "No Action" },
      { dataSourceId: DR, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Mapped", action: "No Action" },
    ],
    nativePreviews: [
      {
        dataSourceId: DR,
        preview:
          "Attach row access rule sales_region IN user.sales_regions to finance.sales.customer_orders.",
      },
    ],
  },
  {
    id: "customer-orders-select-access",
    name: "Customer Orders Select Access",
    status: "POLICY_PARTIAL_DEPLOYED",
    lakehousePolicyType: "Object Access",
    policyDataTypeId: "2",
    description:
      "Grant Finance Analyst read access to the canonical customer orders table in every mapped platform.",
    canonicalObject: "finance.sales.customer_orders",
    target: "finance_analyst",
    rule: "Finance Analyst can SELECT from customer orders.",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: "finance.sales.customer_orders" }],
    privilegeList: [selectPrivilege],
    rules: buildRoleRules("FINANCE_ANALYST"),
    coverageStatus: "Drift Detected",
    lastUpdated: "May 30, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Object Access",
      name: "OBJECT_ACCESS",
      displayName: "Object Access",
    },
    roleName: "Finance Analyst",
    roleMappings: [
      { dataSourceId: SF, nativeRole: "FINANCE_ANALYST", status: "Mapped" },
      { dataSourceId: DR, nativeRole: null, status: "Needs Mapping" },
    ],
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Mapped", action: "No Action" },
      { dataSourceId: DR, objectExists: true, policyStatus: "Missing", roleMappingStatus: "Needs Mapping", action: "Map Role" },
    ],
    nativePreviews: [
      { dataSourceId: SF, preview: "GRANT SELECT ON TABLE FINANCE.SALES.CUSTOMER_ORDERS TO ROLE FINANCE_ANALYST;" },
      { dataSourceId: DR, preview: "Grant SELECT on finance.sales.customer_orders to role finance_analyst;" },
    ],
    policyActions: [resolveDriftAction, deployAction, editAction, deleteAction],
  },
  {
    id: "opportunities-sales-access",
    name: "Customer Opportunities Sales Access",
    status: "POLICY_DEPLOYED",
    lakehousePolicyType: "Object Access",
    policyDataTypeId: "2",
    description: "Grant Sales Ops read access to customer opportunities.",
    canonicalObject: "finance.sales.customer_opportunities",
    target: "sales_ops",
    rule: "Sales Ops can SELECT from customer opportunities.",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: "finance.sales.customer_opportunities" }],
    privilegeList: [selectPrivilege],
    rules: buildRoleRules("SALES_OPS"),
    coverageStatus: "Fully Covered",
    lastUpdated: "May 28, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Object Access",
      name: "OBJECT_ACCESS",
      displayName: "Object Access",
    },
    policyActions: [redeployAction, disableAction, editAction, deleteAction],
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Mapped", action: "No Action" },
      { dataSourceId: DR, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Mapped", action: "No Action" },
    ],
    nativePreviews: [
      { dataSourceId: SF, preview: "GRANT SELECT ON TABLE FINANCE.SALES.CUSTOMER_OPPORTUNITIES TO ROLE SALES_OPS;" },
      { dataSourceId: DR, preview: "Grant SELECT on finance.sales.customer_opportunities to role sales_ops;" },
    ],
  },
  {
    id: "lead-email-masking",
    name: "Lead Email Masking",
    status: "POLICY_PARTIAL_DEPLOYED",
    lakehousePolicyType: "Column Masking",
    policyDataTypeId: "3",
    description: "Mask marketing lead email addresses in mapped campaign tables.",
    canonicalObject: "marketing.campaigns.leads",
    target: "email",
    rule: "Mask lead email outside marketing operations.",
    dataSourceIds: [SF, DR],
    databaseObjects: [{ names: "marketing.campaigns.leads" }],
    privilegeList: [],
    rules: buildColumnConditionRules("email", "email"),
    coverageStatus: "Partial Coverage",
    lastUpdated: "May 27, 2026",
    owner: "Maya Patel",
    lakehousePolicyTypeDetails: {
      id: "Masking",
      name: "MASKING",
      displayName: "Column Masking",
    },
    policyActions: [deployAction, editAction, deleteAction],
    platformCoverage: [
      { dataSourceId: SF, objectExists: true, policyStatus: "Deployed", roleMappingStatus: "Not Required", action: "No Action" },
      { dataSourceId: DR, objectExists: false, policyStatus: "Missing", roleMappingStatus: "Not Required", action: "Review Deployment Plan" },
    ],
    nativePreviews: [
      { dataSourceId: SF, preview: "CREATE MASKING POLICY LEAD_EMAIL_MASK AS (val STRING) RETURNS STRING -> SHA2(val);" },
      { dataSourceId: DR, preview: "Apply column mask on marketing.campaigns.leads.email for unauthorized roles." },
    ],
  },
];

export const lakehouseSeed: LakehouseSeed = {
  policies,
};

export default lakehouseSeed;
